import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  getFirestore, 
  doc, 
  getDoc, 
  updateDoc, 
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  addDoc
} from 'firebase/firestore';
import { useAuth } from '../../auth/AuthContext';
import Sidebar from '../../partials/Sidebar';
import Header from '../../partials/Header';
import ForumLeftContent from '../../partials/community/ForumLeftContent';
import ForumEntry from '../../partials/community/ForumEntry';

function ForumPost() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState('home');
  const [loading, setLoading] = useState(true);
  const [post, setPost] = useState(null);
  const [hasUpvoted, setHasUpvoted] = useState(false);
  const { postId } = useParams();
  const navigate = useNavigate();
  const db = getFirestore();
  const { user } = useAuth();

  const handleUpvote = async () => {
    if (!user) return;
    
    try {
      const postRef = doc(db, 'posts', postId);
      const upvotesRef = collection(db, 'upvotes');
      
      setPost(prev => ({
        ...prev,
        upvotes: hasUpvoted ? prev.upvotes - 1 : prev.upvotes + 1
      }));
      setHasUpvoted(!hasUpvoted);

      if (hasUpvoted) {
        const q = query(upvotesRef, 
          where('userId', '==', user.uid),
          where('postId', '==', postId)
        );
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          await deleteDoc(doc(db, 'upvotes', snapshot.docs[0].id));
        }
      } else {
        await addDoc(upvotesRef, {
          userId: user.uid,
          postId: postId,
          timestamp: new Date()
        });
      }

      await updateDoc(postRef, {
        upvotes: hasUpvoted ? post.upvotes - 1 : post.upvotes + 1
      });
    } catch (error) {
      console.error('Error updating upvote:', error);
      setPost(prev => ({
        ...prev,
        upvotes: hasUpvoted ? prev.upvotes + 1 : prev.upvotes - 1
      }));
      setHasUpvoted(!hasUpvoted);
    }
  };

  useEffect(() => {
    const fetchPost = async () => {
      if (!postId) {
        navigate('/community/forum');
        return;
      }

      try {
        const postDoc = await getDoc(doc(db, 'posts', postId));
        if (!postDoc.exists()) {
          navigate('/community/forum');
          return;
        }
        const postData = postDoc.data();
        setPost({ id: postDoc.id, ...postData });
        
        if (user) {
          const upvotesRef = collection(db, 'upvotes');
          const q = query(upvotesRef, 
            where('userId', '==', user.uid),
            where('postId', '==', postId)
          );
          const snapshot = await getDocs(q);
          setHasUpvoted(!snapshot.empty);
        }
      } catch (error) {
        console.error('Error fetching post:', error);
        navigate('/community/forum');
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [postId, db, navigate, user]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500"></div>
      </div>
    );
  }

  return (
    <div className="flex h-[100dvh] overflow-hidden">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
        <main className="grow">
          <div className="px-4 sm:px-6 lg:px-8 py-8 md:py-0 w-full max-w-9xl mx-auto">
            <div className="xl:flex">
              <div className="md:flex flex-1">
                <ForumLeftContent setActiveFilter={setActiveFilter} />
                <div className="flex-1 md:ml-8 xl:mx-4 2xl:mx-8">
                  <div className="md:py-8">
                    {post && (
                      <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl p-5 mb-4">
                        <ForumEntry 
                          postId={postId} 
                          post={post}
                          hasUpvoted={hasUpvoted}
                          onUpvote={handleUpvote}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default ForumPost;